/**
 * Field Context Menu Component
 *
 * Small popup menu that appears when clicking on a builder form field
 * Shows edit and delete options with on-brand styling
 */

import React, { useEffect, useRef } from 'react';
import { ReactComponent as EditIcon } from '../../shared/icons/edit.svg';
import { ReactComponent as TrashIcon } from '../../shared/icons/trashcan-icon.svg';
import styles from './FieldContextMenu.module.scss';

export interface FieldContextMenuProps {
  x: number;
  y: number;
  fieldType: 'textfield' | 'checkbox' | 'strikeout';
  onEdit?: () => void;
  onDelete: () => void;
  onClose: () => void;
}

export const FieldContextMenu: React.FC<FieldContextMenuProps> = ({
  x,
  y,
  fieldType,
  onEdit,
  onDelete,
  onClose,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  /**
   * Close menu when clicking outside
   */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    // Add small delay to prevent immediate close from the click that opened it
    setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  /**
   * Close on Escape key
   */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      ref={menuRef}
      className={styles.contextMenu}
      style={{
        left: `${x}px`,
        top: `${y}px`,
      }}
    >
      {/* Only show edit button for text fields (not strikeouts or checkboxes) */}
      {fieldType === 'textfield' && onEdit && (
        <button
          className={styles.menuItem}
          onClick={() => {
            onEdit();
            onClose();
          }}
          title="Edit field"
        >
          <EditIcon className={`${styles.icon} ${styles.editIcon}`} />
        </button>
      )}
      <button
        className={styles.menuItem}
        onClick={() => {
          onDelete();
          onClose();
        }}
        title="Delete field"
      >
        <TrashIcon className={`${styles.icon} ${styles.trashIcon}`} />
      </button>
    </div>
  );
};
